import React, { useState } from 'react';
import { PayPalButtons, PayPalScriptProvider } from '@paypal/react-paypal-js';
import { PAYPAL_CONFIG } from '../config/paypal';
import { processPayPalPayment, getPayPalPackage } from '../services/paypalService';
import { User } from '@supabase/supabase-js';

interface PayPalCheckoutProps {
  packageId: string;
  amount: number;
  userId: string;  // User ID en lugar de usar hook
  onSuccess?: (vcoins: number) => void;
  onError?: (error: string) => void;
}

/**
 * Componente de checkout de PayPal
 *
 * Muestra los botones de PayPal y maneja el flujo de pago completo
 */
export function PayPalCheckout({ packageId, amount, userId, onSuccess, onError }: PayPalCheckoutProps) {
  const [isPaying, setIsPaying] = useState(false);

  // Opciones iniciales del script de PayPal
  const initialOptions = {
    clientId: PAYPAL_CONFIG.clientId,
    currency: PAYPAL_CONFIG.currency,
    intent: PAYPAL_CONFIG.intent
  };

  return (
    <PayPalScriptProvider options={initialOptions}>
      <div className="w-full">
        {isPaying && (
          <div className="text-center py-4">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-500 mx-auto mb-2"></div>
            <p className="text-sm text-gray-400">Procesando pago...</p>
          </div>
        )}

        <PayPalButtons
          style={{
            layout: 'vertical',
            color: 'gold',
            shape: 'rect',
            label: 'paypal'
          }}
          disabled={isPaying}
          createOrder={(data, actions) => {
            return actions.order.create({
              purchase_units: [
                {
                  amount: {
                    value: amount.toFixed(2),
                    currency_code: 'USD'
                  },
                  description: `VCoins Package - ${packageId}`
                }
              ],
              intent: 'CAPTURE'
            });
          }}
          onApprove={async (data, actions) => {
            try {
              setIsPaying(true);
              console.log('[PayPal] Orden aprobada:', data);

              // Capturar el pago
              const order = await actions.order!.capture();
              console.log('[PayPal] Pago capturado:', order);

              // Verificar que tengamos un usuario autenticado
              if (!userId) {
                throw new Error('Usuario no autenticado');
              }

              // Extraer información del pago
              const captureDetails = order.purchase_units[0].payments.captures[0];
              const payerInfo = order.payer;

              // Procesar el pago en nuestro backend
              const result = await processPayPalPayment(userId, packageId, {
                orderId: order.id,
                payerId: payerInfo.payer_id || '',
                payerEmail: payerInfo.email_address || '',
                amount: parseFloat(captureDetails.amount.value),
                currency: captureDetails.amount.currency_code,
                status: captureDetails.status
              });

              if (result.success) {
                console.log('[PayPal] ✅ Pago procesado exitosamente');
                onSuccess?.(result.vcoins || 0);
              } else {
                throw new Error(result.message);
              }
            } catch (error: any) {
              console.error('[PayPal] ❌ Error al procesar pago:', error);
              onError?.(error.message || 'Error al procesar el pago');
            } finally {
              setIsPaying(false);
            }
          }}
          onError={(err) => {
            console.error('[PayPal] Error en el flujo de pago:', err);
            setIsPaying(false);
            onError?.('Error al procesar el pago. Por favor intenta nuevamente.');
          }}
          onCancel={() => {
            console.log('[PayPal] Pago cancelado por el usuario');
            setIsPaying(false);
          }}
        />
      </div>
    </PayPalScriptProvider>
  );
}

/**
 * Componente de tarjeta de paquete con PayPal integrado
 */
interface PayPalPackageCardProps {
  packageId: string;
  name: string;
  description: string;
  vcoins: number;
  amount: number;
  popular?: boolean;
  recommended?: boolean;
  bestValue?: boolean;
}

export function PayPalPackageCard({
  packageId,
  name,
  description,
  vcoins,
  amount,
  popular,
  recommended,
  bestValue
}: PayPalPackageCardProps) {
  const [showPayPal, setShowPayPal] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const handleSuccess = (vcoinsAdded: number) => {
    setSuccessMessage(`¡Pago exitoso! ${vcoinsAdded} VCoins agregadas a tu cuenta.`);
    setShowPayPal(false);
    // Recargar después de 2 segundos
    setTimeout(() => {
      window.location.reload();
    }, 2000);
  };

  const handleError = (error: string) => {
    setErrorMessage(error);
    setTimeout(() => setErrorMessage(null), 5000);
  };

  return (
    <div className="relative bg-gray-900/50 backdrop-blur-sm rounded-xl p-6 border border-gray-800 hover:border-purple-500/50 transition-all">
      {/* Badge */}
      {(popular || recommended || bestValue) && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2">
          <span className="bg-gradient-to-r from-purple-600 to-pink-600 text-white text-xs font-bold px-3 py-1 rounded-full">
            {popular && 'MÁS POPULAR'}
            {recommended && 'RECOMENDADO'}
            {bestValue && 'MEJOR VALOR'}
          </span>
        </div>
      )}

      {/* Contenido */}
      <div className="text-center space-y-4">
        <h3 className="text-2xl font-bold text-white">{name}</h3>
        <p className="text-gray-400 text-sm">{description}</p>

        {/* Precio */}
        <div className="py-4">
          <div className="text-4xl font-bold text-white">
            ${amount.toFixed(2)} <span className="text-lg text-gray-400">USD</span>
          </div>
        </div>

        {/* VCoins */}
        <div className="bg-gray-800/50 rounded-lg p-4">
          <div className="text-3xl font-bold text-purple-400">{vcoins}</div>
          <div className="text-sm text-gray-400">VCoins</div>
        </div>

        {/* Mensajes */}
        {successMessage && (
          <div className="bg-green-500/20 border border-green-500 text-green-400 rounded-lg p-3 text-sm">
            {successMessage}
          </div>
        )}

        {errorMessage && (
          <div className="bg-red-500/20 border border-red-500 text-red-400 rounded-lg p-3 text-sm">
            {errorMessage}
          </div>
        )}

        {/* Botón o PayPal */}
        {!showPayPal ? (
          <button
            onClick={() => setShowPayPal(true)}
            className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white font-semibold py-3 rounded-lg transition-all transform hover:scale-105"
          >
            Comprar ahora
          </button>
        ) : (
          <div className="space-y-2">
            <PayPalCheckout
              packageId={packageId}
              amount={amount}
              onSuccess={handleSuccess}
              onError={handleError}
            />
            <button
              onClick={() => setShowPayPal(false)}
              className="w-full text-gray-400 hover:text-white text-sm py-2 transition-colors"
            >
              Cancelar
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
